import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EmailGate from "@/components/EmailGate";
import ProjectHeader from "@/components/ProjectHeader";
import Toolbar from "@/components/Toolbar";
import TaskTable, { BASE_COLS } from "@/components/TaskTable";
import GanttTimeline from "@/components/GanttTimeline";
import ShareDialog from "@/components/ShareDialog";
import VersionHistoryDialog from "@/components/VersionHistoryDialog";
import SaveVersionDialog from "@/components/SaveVersionDialog";
import EventPopover from "@/components/EventPopover";
import RiskHeatmap from "@/components/RiskHeatmap";
import ColumnsDialog from "@/components/ColumnsDialog";
import StatusesDialog from "@/components/StatusesDialog";
import { getStatuses } from "@/lib/gantt-utils";
import ImportDialog from "@/components/ImportDialog";
import HelpDrawer from "@/components/HelpDrawer";
import HaveAQuestionBadge from "@/components/HaveAQuestionBadge";
import ProjectAccessDenied from "@/components/ProjectAccessDenied";
import ProjectLoadingScreen from "@/components/ProjectLoadingScreen";
import TaskDetailDialog from "@/components/TaskDetailDialog";
import useLeftPane from "@/hooks/useLeftPane";
import useColumnPreferences from "@/hooks/useColumnPreferences";
import useTaskHierarchy from "@/hooks/useTaskHierarchy";
import useBulkSelection from "@/hooks/useBulkSelection";
import useSavedViews from "@/hooks/useSavedViews";
import useSyncedScroll from "@/hooks/useSyncedScroll";
import {
  addTaskEvent,
  createTask,
  createVersion,
  deleteTask,
  deleteTaskEvent,
  fetchProject,
  restoreVersion,
  updateProject,
  updateTask,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ZOOM_LEVELS, computeRange } from "@/lib/gantt-utils";
import { computeCriticalPath } from "@/lib/critical-path";
import { computeProjectHealth } from "@/lib/health";
import ProjectSidebar from "@/components/ProjectSidebar";
import { fetchProjectMembers, reorderTasks } from "@/lib/api";
import useIsMobile from "@/hooks/useIsMobile";
import { Rows, ChartLine } from "@phosphor-icons/react";

export default function ProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Email resolution: logged-in user's email wins, else localStorage gate email
  const gateEmail =
    typeof window !== "undefined"
      ? localStorage.getItem(`pg_email_${projectId}`) || ""
      : "";
  const [email, setEmail] = useState(user?.email || gateEmail || "");
  const [role, setRole] = useState(null);
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      const e = user?.email || localStorage.getItem(`pg_email_${projectId}`) || "";
      setEmail(e);
    }
  }, [user, authLoading, projectId]);

  // Filters + zoom
  const [search, setSearch] = useState("");
  const [workstreamFilter, setWorkstreamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [zoomKey, setZoomKey] = useState("day");

  // Dialog open states
  const [shareOpen, setShareOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [saveVersionOpen, setSaveVersionOpen] = useState(false);
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [statusesOpen, setStatusesOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [eventPopover, setEventPopover] = useState(null);
  const [flashTaskId, setFlashTaskId] = useState(null);
  const [detailTaskId, setDetailTaskId] = useState(null);

  const [criticalPathOn, setCriticalPathOn] = useState(
    () => localStorage.getItem(`pg_critpath_${projectId}`) === "1",
  );
  useEffect(() => {
    localStorage.setItem(`pg_critpath_${projectId}`, criticalPathOn ? "1" : "0");
  }, [criticalPathOn, projectId]);

  // Project sidebar collapse — defaults expanded, persisted per-project.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(`pg_sidebar_${projectId}`) === "1",
  );
  useEffect(() => {
    localStorage.setItem(`pg_sidebar_${projectId}`, sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed, projectId]);

  // Mobile-only state. On screens <768px we (a) hide the persistent sidebar
  // and show it as a slide-in drawer, and (b) replace the side-by-side
  // Table+Chart split with a tab switcher so each view gets the full width.
  const isMobile = useIsMobile(768);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileView, setMobileView] = useState("table"); // "table" | "chart"

  // Collaborator count (project members list)
  const [memberCount, setMemberCount] = useState(null);

  // Drag state — supports both REORDER (drop between rows) and NEST (drop
  // INTO a row to make the source a subtask of the target). Distinguished by
  // cursor X relative to the target row: in the first ~60% (under the task
  // title column) = reorder; in the remaining ~40% (under/right of the
  // Subtask column) = nest.
  const dragSrcRef = useRef(null);
  const dragOverRef = useRef(null);
  const [dragIndicator, setDragIndicator] = useState(null);
  // Shape: { targetId: string, mode: "reorder-before"|"reorder-after"|"nest"|"unnest" }

  // Extracted hooks ------------------------------------------------------
  const leftPane = useLeftPane(projectId);
  const columnPrefs = useColumnPreferences(projectId);
  const hierarchy = useTaskHierarchy(tasks, { search, workstreamFilter, statusFilter });
  const selection = useBulkSelection({
    allTasks: tasks,
    filteredTasks: hierarchy.filteredTasks,
  });
  const scroll = useSyncedScroll();

  const exportContainerRef = useRef(null);
  const canEdit = role === "admin" || role === "edit";

  // --- Data loader ------------------------------------------------------
  const load = useCallback(async () => {
    if (authLoading) return;
    if (!email) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchProject(projectId, email);
      setProject(data.project);
      setTasks(data.tasks || []);
      setRole(data.role);
    } catch (err) {
      if (err?.response?.status === 403) {
        if (!user) {
          localStorage.removeItem(`pg_email_${projectId}`);
          setEmail("");
        }
        toast.error(err?.response?.data?.detail || "You don't have access to this project");
        setRole("denied");
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load project");
      }
    } finally {
      setLoading(false);
    }
  }, [email, projectId, authLoading, user]);

  useEffect(() => {
    load();
  }, [load]);

  // --- Derived ----------------------------------------------------------
  const zoom = ZOOM_LEVELS[zoomKey];
  // Standup meeting markers — pinned to specific tasks via the Gantt context
  // menu shortcut. Survives the 30-day raw-content purge so the cell stays
  // visible long after the session expires. Keyed by task_id → count for
  // cheap row-level lookup inside TaskRow.
  const [meetingMarkersByTask, setMeetingMarkersByTask] = useState({});
  useEffect(() => {
    if (!projectId) return;
    let live = true;
    import("@/lib/standup-api").then(({ standupApi }) => {
      standupApi.ganttMarkers(projectId).then((r) => {
        if (!live) return;
        const map = {};
        (r.data.markers || []).forEach((m) => {
          if (!m.task_id) return;
          if (!map[m.task_id]) map[m.task_id] = [];
          map[m.task_id].push(m);
        });
        setMeetingMarkersByTask(map);
      }).catch(() => {});
    });
    return () => { live = false; };
  }, [projectId, tasks.length]);

  const range = useMemo(() => computeRange(project, tasks), [project, tasks]);
  const criticalPath = useMemo(
    () => computeCriticalPath(tasks, project?.date_columns || []),
    [tasks, project],
  );

  // Project health stoplight (per iter-25 spec). Recomputes whenever tasks
  // change. Returns {state, label, counts, worst}.
  const health = useMemo(
    () => computeProjectHealth(tasks, project),
    [tasks, project],
  );

  // Fetch collaborator count once project is loaded.
  useEffect(() => {
    let cancelled = false;
    if (!project?.id || !canEdit) return;
    (async () => {
      try {
        const members = await fetchProjectMembers(project.id);
        if (!cancelled) setMemberCount(members.length);
      } catch (_) {
        if (!cancelled) setMemberCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [project?.id, canEdit]);

  // --- Scroll-to-today plumbing (tiny enough to stay inline) -----------
  const scrollToTodayRef = useRef(null);
  const registerScrollToToday = (fn) => {
    scrollToTodayRef.current = fn;
  };
  const goToToday = () => scrollToTodayRef.current?.();

  // Drag handlers — power both row-reorder and drag-to-nest.
  //
  // Detection rule (in `over`): the row is split into two zones by its
  // horizontal mid-point.
  //   • Cursor in LEFT half  → reorder. `mode` is "reorder-before" if the
  //     cursor is in the top half of the row, "reorder-after" otherwise.
  //   • Cursor in RIGHT half → nest. The source becomes a subtask of the
  //     target. The target itself can be a parent OR a subtask (the backend
  //     supports arbitrary parent_id).
  //
  // `unnest` (subtask → root task) is achieved by dragging onto an empty
  // band at the very top of the table — currently a stretch goal; for now
  // users can clear the parent via the task detail dialog.
  const reorderHandlers = useMemo(() => ({
    start: (e, taskId) => {
      dragSrcRef.current = taskId;
      try {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", taskId);
      } catch (_) { /* some browsers throw if dataTransfer is read-only */ }
    },
    over: (e, taskId) => {
      const sourceId = dragSrcRef.current;
      if (!sourceId || sourceId === taskId) return;
      // Reject obvious cycles — can't nest a parent INTO its own child.
      const targetTask = tasks.find((t) => t.id === taskId);
      const sourceTask = tasks.find((t) => t.id === sourceId);
      if (!targetTask || !sourceTask) return;
      // Walk up the target's ancestry; if we hit the source, it's a cycle.
      let cur = targetTask;
      while (cur?.parent_id) {
        if (cur.parent_id === sourceId) return;
        cur = tasks.find((t) => t.id === cur.parent_id);
      }
      e.preventDefault();
      dragOverRef.current = taskId;
      // Compute the cursor's position WITHIN the row to choose mode.
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const isNest = x > rect.width * 0.5;
      let mode;
      if (isNest) mode = "nest";
      else mode = y < rect.height / 2 ? "reorder-before" : "reorder-after";
      setDragIndicator({ targetId: taskId, mode });
    },
    drop: async (e, targetId) => {
      e.preventDefault();
      const sourceId = dragSrcRef.current;
      const indicator = dragIndicator;
      dragSrcRef.current = null;
      dragOverRef.current = null;
      setDragIndicator(null);
      if (!sourceId || sourceId === targetId || !indicator) return;
      const sourceTask = tasks.find((t) => t.id === sourceId);
      const targetTask = tasks.find((t) => t.id === targetId);
      if (!sourceTask || !targetTask) return;

      // ---------- NEST: source becomes child of target ----------
      if (indicator.mode === "nest") {
        // Optimistic UI — flip parent_id locally first.
        setTasks((prev) => prev.map((t) =>
          t.id === sourceId ? { ...t, parent_id: targetId } : t
        ));
        try {
          const { updateTask } = await import("@/lib/api");
          await updateTask(sourceId, { parent_id: targetId }, email);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Couldn't nest task — reloading");
          load();
        }
        return;
      }

      // ---------- REORDER: move source before/after target ----------
      // The reorder operates within the source's current peer set (rows with
      // the same parent_id). If the source and target have different parents,
      // we first re-parent the source to the target's parent so the move is
      // visually sensible.
      let workingTasks = tasks;
      if (sourceTask.parent_id !== targetTask.parent_id) {
        // Re-parent the source first.
        const newParent = targetTask.parent_id || null;
        workingTasks = tasks.map((t) =>
          t.id === sourceId ? { ...t, parent_id: newParent } : t
        );
        setTasks(workingTasks);
        try {
          const { updateTask } = await import("@/lib/api");
          await updateTask(sourceId, { parent_id: newParent }, email);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Reorder failed — reloading");
          load();
          return;
        }
      }
      const parentId = targetTask.parent_id || null;
      const peers = workingTasks
        .filter((t) => (t.parent_id || null) === parentId)
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
      const srcIdx = peers.findIndex((t) => t.id === sourceId);
      const dstIdx = peers.findIndex((t) => t.id === targetId);
      if (srcIdx === -1 || dstIdx === -1) return;
      const [moved] = peers.splice(srcIdx, 1);
      // After splice the target index may have shifted by 1 if source was
      // earlier; recompute.
      const newDstIdx = peers.findIndex((t) => t.id === targetId);
      const insertAt = indicator.mode === "reorder-before" ? newDstIdx : newDstIdx + 1;
      peers.splice(insertAt, 0, moved);
      const items = peers.map((t, i) => ({ id: t.id, order_index: (i + 1) * 1000 }));
      setTasks((prev) => {
        const next = prev.map((t) => {
          const found = items.find((x) => x.id === t.id);
          return found ? { ...t, order_index: found.order_index } : t;
        });
        return next.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
      });
      try {
        await reorderTasks(projectId, items, email);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Reorder failed — reloading");
        load();
      }
    },
    end: () => {
      dragSrcRef.current = null;
      dragOverRef.current = null;
      setDragIndicator(null);
    },
    // Expose the live indicator so TaskRow can render the visual feedback.
    indicator: dragIndicator,
  }), [tasks, projectId, email, load, dragIndicator]);

  const handleToggleHealth = () => {
    const next = !(project?.health_tracker_enabled !== false);
    handleUpdateProject({ health_tracker_enabled: next });
  };

  // --- Project / Task mutations ----------------------------------------
  const handleUpdateProject = useCallback(
    async (patch) => {
      try {
        const updated = await updateProject(projectId, patch, email);
        setProject(updated);
        toast.success("Project updated");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Update failed");
      }
    },
    [projectId, email],
  );

  const renameColumn = useCallback(
    async (key, newLabel) => {
      const next = { ...(project?.column_labels || {}), [key]: newLabel };
      await handleUpdateProject({ column_labels: next });
    },
    [project, handleUpdateProject],
  );
  const renameDateColumn = useCallback(
    async (colId, newLabel) => {
      const nextCols = (project?.date_columns || []).map((c) =>
        c.id === colId ? { ...c, label: newLabel } : c,
      );
      await handleUpdateProject({ date_columns: nextCols });
    },
    [project, handleUpdateProject],
  );

  const handleAddTask = async () => {
    try {
      const t = await createTask(projectId, { task: "New Task" }, email);
      setTasks((prev) => [...prev, t]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add task");
    }
  };

  const handleAddSubtask = async (parentId, names) => {
    const parent = tasks.find((t) => t.id === parentId);
    const nameList = Array.isArray(names) && names.length > 0 ? names : ["New Subtask"];
    try {
      const created = [];
      for (const name of nameList) {
        const t = await createTask(
          projectId,
          {
            subtask: (name || "").trim() || "New Subtask",
            parent_id: parentId,
            workstream: parent?.workstream || "",
          },
          email,
        );
        created.push(t);
      }
      setTasks((prev) => [...prev, ...created]);
      hierarchy.setCollapsed((c) => ({ ...c, [parentId]: false }));
      if (created.length > 1) toast.success(`Added ${created.length} subtasks`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add subtask");
    }
  };

  const handleUpdateTask = async (taskId, patch) => {
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId
          ? { ...t, ...patch, dates: { ...(t.dates || {}), ...(patch.dates || {}) } }
          : t,
      ),
    );
    try {
      const updated = await updateTask(taskId, patch, email);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
      load();
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId, email);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success("Task deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const handleBulkDelete = async () => {
    if (selection.selectedIds.size === 0) return;
    const ids = selection.expandedSelection();
    const count = ids.size;
    if (
      !window.confirm(
        `Delete ${count} task${count === 1 ? "" : "s"}? This cannot be undone.`,
      )
    ) {
      return;
    }
    let success = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await deleteTask(id, email);
        success += 1;
      } catch {
        failed += 1;
      }
    }
    setTasks((prev) => prev.filter((t) => !ids.has(t.id)));
    selection.exit();
    if (failed === 0) toast.success(`Deleted ${success} task${success === 1 ? "" : "s"}`);
    else toast.error(`Deleted ${success}, ${failed} failed`);
  };

  const handleAddEvent = async (taskId, payload) => {
    try {
      const updated = await addTaskEvent(taskId, { ...payload, author_email: email }, email);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
      toast.success("Event added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add event");
    }
  };
  const handleDeleteEvent = async (taskId, eventId) => {
    try {
      const updated = await deleteTaskEvent(taskId, eventId, email);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const handleSaveVersion = async (note) => {
    try {
      await createVersion(projectId, note, email);
      toast.success("Version saved");
      setSaveVersionOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };
  const handleRestoreVersion = async (versionId) => {
    try {
      await restoreVersion(projectId, versionId, email);
      toast.success("Version restored");
      setVersionsOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to restore");
    }
  };

  // --- Saved views wiring ----------------------------------------------
  const savedViews = useSavedViews({
    project,
    email,
    updateProject: handleUpdateProject,
    currentState: () => ({
      search,
      workstream_filter: workstreamFilter,
      status_filter: statusFilter,
      zoom_key: zoomKey,
      critical_path_on: criticalPathOn,
      left_collapsed: leftPane.collapsed,
      hidden_cols: Array.from(columnPrefs.hiddenCols),
      col_widths: { ...columnPrefs.colWidths },
    }),
    applyState: (v) => {
      setSearch(v.search || "");
      setWorkstreamFilter(v.workstream_filter || "");
      setStatusFilter(v.status_filter || "");
      if (v.zoom_key) setZoomKey(v.zoom_key);
      setCriticalPathOn(!!v.critical_path_on);
      leftPane.setCollapsed(!!v.left_collapsed);
      columnPrefs.setHiddenCols(new Set(v.hidden_cols || []));
      columnPrefs.setColWidths({ ...(v.col_widths || {}) });
    },
  });

  // --- Gate screens -----------------------------------------------------
  if (!email) {
    return (
      <EmailGate
        projectId={projectId}
        onGranted={(em, r) => {
          setEmail(em);
          setRole(r);
        }}
      />
    );
  }
  if (role === "denied") return <ProjectAccessDenied isAuthed={!!user} />;
  if (loading || !project) return <ProjectLoadingScreen />;

  // Combined column list for the View menu (base + date + custom columns)
  const viewColumns = [
    ...BASE_COLS.map((c) => ({ key: c.key, label: c.label })),
    ...(project.date_columns || []).map((c) => ({ key: `date:${c.id}`, label: c.label })),
    ...(project.custom_columns || []).map((c) => ({ key: `custom:${c.id}`, label: c.label })),
  ];

  return (
    <div
      className="h-screen w-full flex flex-col overflow-hidden bg-slate-50"
      data-testid="project-view"
    >
      <ProjectHeader
        project={project}
        role={role}
        email={email}
        onUpdate={handleUpdateProject}
        onOpenShare={() => setShareOpen(true)}
        onOpenVersions={() => setVersionsOpen(true)}
        onSaveVersion={() => setSaveVersionOpen(true)}
        exportRef={exportContainerRef}
        health={health}
        memberCount={memberCount}
        onToggleHealth={handleToggleHealth}
      />

      <Toolbar
        search={search}
        setSearch={setSearch}
        workstreamFilter={workstreamFilter}
        setWorkstreamFilter={setWorkstreamFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        statuses={getStatuses(project)}
        onOpenStatuses={() => setStatusesOpen(true)}
        workstreams={Array.from(new Set(tasks.map((t) => t.workstream).filter(Boolean)))}
        onClear={() => {
          setSearch("");
          setWorkstreamFilter("");
          setStatusFilter("");
        }}
        zoomKey={zoomKey}
        setZoomKey={setZoomKey}
        canEdit={canEdit}
        onAddTask={handleAddTask}
        leftCollapsed={leftPane.collapsed}
        onToggleLeftPane={leftPane.toggleCollapsed}
        onOpenHeatmap={() => setHeatmapOpen(true)}
        onOpenColumns={() => setColumnsOpen(true)}
        onOpenImport={() => setImportOpen(true)}
        onOpenHelp={() => setHelpOpen(true)}
        criticalPathOn={criticalPathOn}
        onToggleCriticalPath={() => setCriticalPathOn((v) => !v)}
        selectMode={selection.selectMode}
        onToggleSelectMode={selection.toggleMode}
        selectedCount={selection.selectedIds.size}
        onBulkDelete={handleBulkDelete}
        viewColumns={viewColumns}
        hiddenCols={columnPrefs.hiddenCols}
        onToggleColumn={columnPrefs.toggleColumnVisibility}
        onShowAllColumns={columnPrefs.showAllColumns}
        onResetColumnWidths={columnPrefs.resetColumnWidths}
        onGoToToday={goToToday}
        savedViews={project.saved_views || []}
        onApplySavedView={savedViews.applyView}
        onSaveCurrentView={savedViews.saveView}
        onDeleteSavedView={savedViews.deleteView}
        canEditProject={canEdit}
      />

      <div
        ref={exportContainerRef}
        className="flex-1 overflow-hidden flex relative border-t border-slate-200"
      >
        {/* Sidebar: persistent on desktop, slide-in drawer on mobile.
            The mobile variant uses an overlay backdrop and absolute
            positioning so it doesn't push the rest of the layout. */}
        {isMobile ? (
          mobileSidebarOpen && (
            <>
              <div
                className="fixed inset-0 bg-slate-950/40 z-30"
                onClick={() => setMobileSidebarOpen(false)}
                aria-label="Close sidebar"
                data-testid="mobile-sidebar-backdrop"
              />
              <div className="fixed top-0 bottom-0 left-0 z-40 bg-white shadow-xl">
                <ProjectSidebar
                  collapsed={false}
                  onToggleCollapsed={() => setMobileSidebarOpen(false)}
                  projectId={projectId}
                  onOpenColumns={() => { setColumnsOpen(true); setMobileSidebarOpen(false); }}
                  onOpenVersions={() => { setVersionsOpen(true); setMobileSidebarOpen(false); }}
                  onOpenHeatmap={() => { setHeatmapOpen(true); setMobileSidebarOpen(false); }}
                  onOpenSupport={() => { setHelpOpen(true); setMobileSidebarOpen(false); }}
                />
              </div>
            </>
          )
        ) : (
          <ProjectSidebar
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
            projectId={projectId}
            onOpenColumns={() => setColumnsOpen(true)}
            onOpenVersions={() => setVersionsOpen(true)}
            onOpenHeatmap={() => setHeatmapOpen(true)}
            onOpenSupport={() => setHelpOpen(true)}
          />
        )}

        {/* Mobile: stack table OR chart based on the toggle. Desktop: side
            by side with the resizer in between. */}
        {isMobile ? (
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-200 bg-white" data-testid="mobile-view-toggle">
              <button
                type="button"
                onClick={() => setMobileSidebarOpen(true)}
                className="h-9 w-9 rounded-md border border-slate-300 flex items-center justify-center text-slate-600 mr-2"
                aria-label="Open menu"
                data-testid="mobile-open-sidebar"
              >
                <Rows size={16} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => setMobileView("table")}
                className={`flex-1 h-9 rounded-md text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                  mobileView === "table"
                    ? "bg-slate-950 text-white"
                    : "bg-white text-slate-600 border border-slate-300"
                }`}
                data-testid="mobile-view-table"
              >
                <Rows size={12} weight="bold" />
                Table
              </button>
              <button
                type="button"
                onClick={() => setMobileView("chart")}
                className={`flex-1 h-9 rounded-md text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                  mobileView === "chart"
                    ? "bg-slate-950 text-white"
                    : "bg-white text-slate-600 border border-slate-300"
                }`}
                data-testid="mobile-view-chart"
              >
                <ChartLine size={12} weight="bold" />
                Chart
              </button>
            </div>
            {mobileView === "table" ? (
              <div className="flex-1 min-w-0 overflow-hidden">
                <TaskTable
                  tasks={hierarchy.filteredTasks}
                  allTasks={tasks}
                  allTasksCount={tasks.length}
                  project={project}
                  canEdit={canEdit}
                  onUpdateTask={handleUpdateTask}
                  onDeleteTask={handleDeleteTask}
                  onAddTask={handleAddTask}
                  onAddSubtask={handleAddSubtask}
                  scrollRef={scroll.tableRef}
                  onScroll={scroll.onTableScroll}
                  flashTaskId={flashTaskId}
                  criticalIds={criticalPathOn ? criticalPath.criticalIds : null}
                  collapsed={hierarchy.collapsed}
                  onToggleCollapse={hierarchy.toggleCollapse}
                  childrenByParent={hierarchy.childrenByParent}
                  selectMode={selection.selectMode}
                  selectedIds={selection.selectedIds}
                  onToggleSelect={selection.toggleRow}
                  onToggleSelectAll={selection.toggleSelectAll}
                  colWidths={columnPrefs.colWidths}
                  hiddenCols={columnPrefs.hiddenCols}
                  onResizeColumn={columnPrefs.resizeColumn}
                  onRenameColumn={canEdit ? renameColumn : null}
                  onRenameDateColumn={canEdit ? renameDateColumn : null}
                  onOpenDetail={(id) => setDetailTaskId(id)}
                  onReorderRows={canEdit ? reorderHandlers : null}
                />
              </div>
            ) : (
              <div className="flex-1 min-w-0 overflow-hidden">
                <GanttTimeline
                  tasks={hierarchy.filteredTasks}
                  project={project}
                  zoom={zoom}
                  range={range}
                  canEdit={canEdit}
                  criticalPath={criticalPath}
                  criticalPathOn={criticalPathOn}
                  onOpenEventPopover={(data) => setEventPopover(data)}
                  onDeleteEvent={handleDeleteEvent}
                  scrollRef={scroll.ganttRef}
                  onScroll={scroll.onGanttScroll}
                  registerScrollToToday={registerScrollToToday}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            {!leftPane.collapsed && (
              <>
                <div
                  className="shrink-0 min-w-[280px] max-w-[1100px] relative"
                  style={{ width: leftPane.width }}
                >
                  <TaskTable
                    tasks={hierarchy.filteredTasks}
                    allTasks={tasks}
                    allTasksCount={tasks.length}
                    project={project}
                    canEdit={canEdit}
                    onUpdateTask={handleUpdateTask}
                    onDeleteTask={handleDeleteTask}
                    onAddTask={handleAddTask}
                    onAddSubtask={handleAddSubtask}
                    meetingMarkersByTask={meetingMarkersByTask}
                    scrollRef={scroll.tableRef}
                    onScroll={scroll.onTableScroll}
                    flashTaskId={flashTaskId}
                    criticalIds={criticalPathOn ? criticalPath.criticalIds : null}
                    collapsed={hierarchy.collapsed}
                    onToggleCollapse={hierarchy.toggleCollapse}
                    childrenByParent={hierarchy.childrenByParent}
                    selectMode={selection.selectMode}
                    selectedIds={selection.selectedIds}
                    onToggleSelect={selection.toggleRow}
                    onToggleSelectAll={selection.toggleSelectAll}
                    colWidths={columnPrefs.colWidths}
                    hiddenCols={columnPrefs.hiddenCols}
                    onResizeColumn={columnPrefs.resizeColumn}
                    onRenameColumn={canEdit ? renameColumn : null}
                    onRenameDateColumn={canEdit ? renameDateColumn : null}
                    onOpenDetail={(id) => setDetailTaskId(id)}
                    onReorderRows={canEdit ? reorderHandlers : null}
                  />
                </div>
                <div
                  onMouseDown={leftPane.startResize}
                  onDoubleClick={leftPane.resetWidth}
                  className="w-1.5 shrink-0 cursor-col-resize relative group z-20"
                  data-testid="left-pane-resizer"
                  title="Drag to resize · double-click to reset"
                >
                  <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-slate-300 group-hover:bg-slate-950 transition-colors" />
                  <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 bg-slate-950/0 group-hover:bg-slate-950/10 transition-colors" />
                </div>
              </>
            )}

            <GanttTimeline
              tasks={hierarchy.filteredTasks}
              project={project}
              zoom={zoom}
              range={range}
              canEdit={canEdit}
              criticalPath={criticalPath}
              criticalPathOn={criticalPathOn}
              onOpenEventPopover={(data) => setEventPopover(data)}
              onDeleteEvent={handleDeleteEvent}
              scrollRef={scroll.ganttRef}
              onScroll={scroll.onGanttScroll}
              registerScrollToToday={registerScrollToToday}
            />
          </>
        )}
      </div>

      {shareOpen && (
        <ShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          project={project}
          email={email}
          role={role}
          onUpdated={(dist) => setProject((p) => ({ ...p, distribution_list: dist }))}
        />
      )}
      {versionsOpen && (
        <VersionHistoryDialog
          open={versionsOpen}
          onOpenChange={setVersionsOpen}
          projectId={projectId}
          email={email}
          canEdit={canEdit}
          onRestore={handleRestoreVersion}
        />
      )}
      {saveVersionOpen && (
        <SaveVersionDialog
          open={saveVersionOpen}
          onOpenChange={setSaveVersionOpen}
          onSave={handleSaveVersion}
        />
      )}
      {eventPopover && (
        <EventPopover
          data={eventPopover}
          project={project}
          onClose={() => setEventPopover(null)}
          onSubmit={(payload) => {
            handleAddEvent(eventPopover.taskId, payload);
            setEventPopover(null);
          }}
        />
      )}
      {heatmapOpen && (
        <RiskHeatmap
          open={heatmapOpen}
          onOpenChange={setHeatmapOpen}
          project={project}
          tasks={tasks}
          onJumpToTask={(taskId) => {
            if (leftPane.collapsed) leftPane.setCollapsed(false);
            setFlashTaskId(taskId);
            setTimeout(() => {
              const row = document.querySelector(`[data-testid="task-row-${taskId}"]`);
              if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
              const grow = document.querySelector(`[data-testid="gantt-row-${taskId}"]`);
              if (grow) grow.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 50);
            setTimeout(() => setFlashTaskId(null), 2400);
          }}
        />
      )}
      {columnsOpen && (
        <ColumnsDialog
          open={columnsOpen}
          onOpenChange={setColumnsOpen}
          project={project}
          tasks={tasks}
          canEdit={canEdit}
          onSave={async (patch) => {
            await handleUpdateProject(patch);
          }}
        />
      )}
      {statusesOpen && (
        <StatusesDialog
          open={statusesOpen}
          onOpenChange={setStatusesOpen}
          project={project}
          tasks={tasks}
          canEdit={canEdit}
          onSave={async (statuses) => {
            await handleUpdateProject({ statuses });
            await load();
          }}
        />
      )}
      {importOpen && (
        <ImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          projectId={projectId}
          onImported={load}
        />
      )}
      <HelpDrawer open={helpOpen} onOpenChange={setHelpOpen} projectId={projectId} />
      {detailTaskId && (
        <TaskDetailDialog
          open={!!detailTaskId}
          onOpenChange={(o) => { if (!o) setDetailTaskId(null); }}
          task={tasks.find((t) => t.id === detailTaskId)}
          allTasks={tasks}
          project={project}
          canEdit={canEdit}
          onUpdateTask={handleUpdateTask}
          onAddSubtask={handleAddSubtask}
          taskNumber={(() => {
            // Derive the same display number the table shows (1, 1.1, 1.2, 2, ...)
            let topIdx = 0;
            const childCursor = {};
            for (const t of tasks) {
              if (!t.parent_id) {
                topIdx += 1;
                if (t.id === detailTaskId) return String(topIdx);
              } else {
                const parentNo = (() => {
                  let i = 0;
                  for (const x of tasks) {
                    if (!x.parent_id) {
                      i += 1;
                      if (x.id === t.parent_id) return String(i);
                    }
                  }
                  return "?";
                })();
                childCursor[t.parent_id] = (childCursor[t.parent_id] || 0) + 1;
                if (t.id === detailTaskId) return `${parentNo}.${childCursor[t.parent_id]}`;
              }
            }
            return null;
          })()}
          onJumpToTask={(targetId) => {
            setDetailTaskId(targetId);
            setFlashTaskId(targetId);
            setTimeout(() => {
              const row = document.querySelector(`[data-testid="task-row-${targetId}"]`);
              if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);
            setTimeout(() => setFlashTaskId(null), 2400);
          }}
        />
      )}
      <HaveAQuestionBadge projectId={projectId} compact />
    </div>
  );
}
